import React, { useEffect } from 'react';
import { useQuery } from 'react-query';
import Highcharts from 'highcharts';
import highchartsStock from 'highcharts/modules/stock';
import highchartsExporting from 'highcharts/modules/exporting';
import * as consts from 'config/consts';
import { CurrencyMode } from 'hooks/useCurrencyMode';
import { useModal } from 'hooks/useModal';
import { isLoading } from 'hooks/useAPI';
import { AssetBundleType } from 'components/features/home/Home';
import Loading from 'components/commons/loading/Loading';
import {
  AboutExchangeRateModal,
  useAboutExchangeRateModalContext,
} from 'components/features/modal/AboutExchangeRateModal';
import {
  GetUserAssetResult,
  GetUserBrokerResultItem,
  getAssetHistory,
  getPositions,
} from 'config/api';
import { PortfolioStuffingAssetChart } from './PortfolioStuffingAssetChart';
import { PortfolioStuffingAssetList } from './PortfolioStuffingAssetList';
import {
  AssetHistoryItemsProps,
  ExtraTimeSeriesData,
} from './../../assetStuffing/AssetStuffing';
import { Side } from 'components/features/utils/side';
import { AssetTypeKey } from 'components/features/utils/assetType';
import { AmountColor } from 'components/features/utils/util';
import { PositionItemsProps } from '../../ProfitAndLossStuffing/ProfitAndLossStuffing';

highchartsStock(Highcharts);
highchartsExporting(Highcharts);

export type PortfolioStuffingChartDatum = {
  assetTypeKey: AssetTypeKey;
  items: {
    x: Date;
    y: number | null;
  }[];
};

export type PortfolioStuffingChartProps = {
  data: PortfolioStuffingChartDatum[];
  extraData?: ExtraTimeSeriesData[];
};

export type PortfolioStuffingProps = {
  asset: GetUserAssetResult;
  brokersInfo: GetUserBrokerResultItem[];
  UserBrokerUpdateTime: React.ReactElement;
  assetBundleType: AssetBundleType;
  getIsOutOfDate: (arg: { account: string }) => boolean | null;
  toPreview: (element: React.ReactElement) => void;
  backHome: () => void;
};

export type AssetOrganizedPortfolioProps = {
  assetTypeKey: AssetTypeKey;
  items: Omit<AssetOrganizedPortfolioItemProps, 'assetTypeKey'>[];
};

export type AssetOrganizedPortfolioForeignProps = {
  assetTypeKey: AssetTypeKey;
  items: Omit<
    AssetOrganizedPortfolioItemForeignProps,
    'assetTypeKey' | 'currencyMode'
  >[];
};

export type SimpleOrganized = { simple: true };

export const isSimple = <T,>(
  state: T | SimpleOrganized
): state is SimpleOrganized => {
  if (!(typeof state === 'object')) return false;
  if (state === null) return false;
  return 'simple' in state && state.simple;
};

type AssetOrganizedPortfolioItemPropsBase<T extends CurrencyMode> = {
  brokerID: string | SimpleOrganized;
  assetTypeKey: AssetTypeKey;
  amount: T extends 'JPY' ? string : { JPY: string; FOREIGN: string };
  side: Side | null | SimpleOrganized;
  quantity: number | null | SimpleOrganized;
  quantityUnit: string | SimpleOrganized;
  identifier: string | null | SimpleOrganized;
  name: string | SimpleOrganized;
  profitOrLoss:
    | (T extends 'JPY' ? string : { JPY: string; FOREIGN: string })
    | SimpleOrganized;
  profitOrLossColor:
    | (T extends 'JPY'
        ? AmountColor
        : { JPY: AmountColor; FOREIGN: AmountColor })
    | SimpleOrganized;
  link: string | null | SimpleOrganized;
  refresh: {
    isOutOfDate: boolean;
    account: string;
  } | null;
  unit?: string;
};

export type AssetOrganizedPortfolioItemProps = AssetOrganizedPortfolioItemPropsBase<
  'JPY'
>;

export type AssetOrganizedPortfolioItemForeignProps = AssetOrganizedPortfolioItemPropsBase<
  'FOREIGN'
> & {
  currencyMode: CurrencyMode;
};

export const isHiddenProductTypes = (productType: number): boolean => {
  const productTypeList = [
    consts.productTypes.預金_MRF,
    consts.productTypes.その他,
  ] as number[];

  return productTypeList.includes(productType);
};

export const getDataFetchingState = (
  identifier: string,
  assetTypeKey: AssetTypeKey
): DataFetchingState => {
  return assetTypeKey === consts.AssetType['投資信託'] && identifier === ''
    ? 'Fetching'
    : 'Fetched';
};

export type DataFetchingState = 'Fetching' | 'Fetched';

export const ModalButtonInternal = ({
  isShowMessage,
  className,
  modalStates,
}: {
  isShowMessage: boolean;
  className?: string;
  modalStates: {
    isShowModal: boolean;
    hideModal: () => void;
    onClick?: () => void;
  };
}) => {
  const { state } = useAboutExchangeRateModalContext();
  const modalIconClass = 'icon-help_outline';
  const { onClick } = modalStates;
  if (isLoading(state) || !state.rate.rates.length)
    return (
      <>
        <div className="w-6"></div>
      </>
    );
  return (
    <>
      <div
        {...(onClick
          ? {
              onClick: e => {
                e.stopPropagation();
                onClick();
              },
            }
          : {})}
        className={
          'text-bluegray-500 text-sm cursor-pointer ' + (className ?? '')
        }>
        {isShowMessage && '換算レートについて'}
        <i className={`${modalIconClass} ml-1 text-bluegray-400`} />
      </div>
      <AboutExchangeRateModal
        {...{
          isShowModal: modalStates.isShowModal,
          hideModal: modalStates.hideModal,
        }}
      />
    </>
  );
};

export const AboutExchangeRateModalButton = ({
  isShowMessage,
  className,
}: {
  isShowMessage: boolean;
  className?: string;
}) => {
  const [isShowModal, { hideModal, showModal }] = useModal();
  return (
    <>
      <ModalButtonInternal
        {...{
          isShowMessage,
          className,
          modalStates: {
            isShowModal,
            hideModal,
            onClick: showModal,
          },
        }}
      />
    </>
  );
};

export const PortfolioStuffingAsset = ({
  asset,
  brokersInfo,
  UserBrokerUpdateTime,
  assetBundleType,
  getIsOutOfDate,
  toPreview,
  backHome,
}: PortfolioStuffingProps): React.ReactElement => {
  const title = '保有資産';

  const { data: positionsData, refetch: positionsRefetch } = useQuery(
    'portfolioStuffingAssetPositions',
    () => getPositionsAPI(assetBundleType.key),
    {
      refetchOnWindowFocus: false,
    }
  );

  const {
    data: assetHistoryData,
    refetch: assetHistoryRefetch,
    remove: assetHistoryRemove,
  } = useQuery(
    'portfolioStuffingAassetHistory',
    () => getAssetHistoryAPI(assetBundleType.key),
    {
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    assetHistoryRemove();
    (async () => {
      await positionsRefetch();
      await assetHistoryRefetch();
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetBundleType]);

  return (
    <>
      {assetHistoryData === null || assetHistoryData === undefined ? (
        <div className="bg-bluegray-600">
          <Loading
            loading={true}
            circularLoading={true}
            style={{
              paddingTop: '165px',
              paddingBottom: '165px',
            }}
          />
        </div>
      ) : (
        <PortfolioStuffingAssetChart
          {...{
            assetHistory: assetHistoryData as AssetHistoryItemsProps[],
            UserBrokerUpdateTime,
            asset,
            title,
            toPreview,
            backHome,
          }}
        />
      )}
      {positionsData === null || positionsData === undefined ? (
        <div className="p-4 pb-0 max-w-md mx-auto">
          <div className="text-lg font-bold mt-3 mb-4">{title}</div>
          <Loading
            loading={true}
            circularLoading={true}
            style={{
              paddingTop: '60px',
              paddingBottom: '60px',
            }}
          />
        </div>
      ) : (
        <PortfolioStuffingAssetList
          {...{
            assetBundleType,
            title,
            positions: positionsData as PositionItemsProps[],
            brokersInfo,
            otherAmount: asset.OtherAmount ?? 0,
            getIsOutOfDate,
          }}
        />
      )}
    </>
  );
};

const getPositionsAPI = async (key: number) => {
  const groupID = key === 0 || key === 9999 ? undefined : key.toString();

  const resPositions_ = await getPositions(groupID);
  const resPositions = resPositions_.Items.map(item => {
    return {
      brokerID: item.BrokerID,
      account: item.Account,
      symbol: item.Symbol,
      symbolName: item.SymbolName,
      accountType: item.AccountType,
      productType: item.ProductType,
      cashMargin: item.CashMargin,
      side: item.Side,
      qty: item.Qty,
      price: item.Price,
      currentAmount: item.CurrentAmount,
      currentAmountUSD: item.CurrentAmountUSD,
      profitOrLoss: item.ProfitOrLoss,
      profitOrLossUSD: item.ProfitOrLossUSD,
      validationAmount: item.ValidationAmount,
      diffProfitOrLoss: item.DiffProfitOrLoss,
      diffProfitOrLossUSD: item.DiffProfitOrLossUSD,
      currencyUnit: item.CurrencyUnit,
    };
  });
  return resPositions;
};

const getAssetHistoryAPI = async (key: number) => {
  const groupID = key === 0 || key === 9999 ? undefined : key.toString();

  const resAssetHistory_ = await getAssetHistory(
    undefined,
    undefined,
    groupID,
    true
  );
  const resAssetHistory = resAssetHistory_.Items.map(item => {
    return {
      day: item.Day,
      totalAssetAmount: item.TotalAssetAmount,
      diffTotalAssetAmount: item.DiffTotalAssetAmount,
      diffTotalAssetRate: item.DiffTotalAssetRate,
      marginProfitOrLoss: item.MarginProfitOrLoss,
      totalProfitOrLoss: item.TotalProfitOrLoss,
      diffTotalProfitOrLoss: item.DiffTotalProfitOrLoss,
      diffTotalProfitOrLossRate: item.DiffTotalProfitOrLossRate,
      stockAmount: item.StockAmount,
      marginAmount: item.MarginAmount,
      roboAdvisorAmount: item.RoboAdvisorAmount,
      uSStockAmount: item.USStockAmount,
      investmentTrustAmount: item.InvestmentTrustAmount,
      unlistedStockAmount: item.UnlistedStockAmount,
      etfEtnAmount: item.EtfEtnAmount,
      foreignEtfEtnAmount: item.ForeignEtfEtnAmount,
      reitAmount: item.ReitAmount,
      foreignReitAmount: item.ForeignReitAmount,
      bondAmount: item.BondAmount,
      foreignBondAmount: item.ForeignBondAmount,
      ebAmount: item.EbAmount,
      foreignEbAmount: item.ForeignEbAmount,
      realEstateAmount: item.RealEstateAmount,
      cashAmount: item.CashAmount,
      otherAmount: item.OtherAmount,
      incorrect: item.Incorrect,
      topix: item.Topix ?? null,
    };
  });
  return resAssetHistory;
};
